import { Image, Modal, Text, TouchableOpacity, View } from "react-native";
import React, { useState } from "react";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../utility/AppColors";
import { AppFontStyle } from "../styles/AppFontStyle";
import { AppImages } from "../utility/AppImages";
import Separator from "./Separator";
import ImageComponent from "./ImageComponent";
import CoinView from "./CoinView";
import { toast } from "react-toastify";
import { claimDailyCheckInReward } from "../services/authAPI";
import { storeLoginData } from "../utility/AppStoreData";

export default function CheckInPopup(props) {
  const {
    showModal = false,
    setShowModal,
    onClose,
    checkInData,
    setCheckInData,
    accessToken,
    userProfileAPI,
    totalCoins,
    loginData,
    setShowCustomToast,
  } = props;
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [disableClaim, setDisableClaim] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // New loading state

  const onCloseClick = () => {
    setShowModal(false);
    // Store both the close flag and today's date when manually closed
    loginData.dailyCheckInResult = {
      ...loginData.dailyCheckInResult,
      closePopup: true,
      closePopupDate: new Date().toDateString()
    };
    storeLoginData(loginData);
    if (onClose) onClose();
  };

  async function updateDailyCheckInReward() {
    setIsLoading(true); // Set loading to true when API call starts
    try {
      const data = await claimDailyCheckInReward(accessToken);
      const responseBody = data.body;
      if (data.statusCode === 200) {
        // Update local state with claim response
        setCheckInData(responseBody);
        
        // Merge claim response data with existing login data and set close flags
        loginData.dailyCheckInResult = {
          ...loginData.dailyCheckInResult,
          ...responseBody,
          closePopup: true,
          closePopupDate: new Date().toDateString()
        };
        storeLoginData(loginData);
        
        // Close popup immediately
        setShowModal(false);
        if (onClose) onClose();
        
        // Show success toast
        setShowCustomToast(true);
        
        // Update user profile after popup is closed
        if (userProfileAPI) {
          userProfileAPI(accessToken);
        }
      } else if (data.statusCode === 400) {
        // Handle "No unclaimed coins" or other 400 errors
        toast.error(responseBody.message || "There was an issue claiming coins. You can claim later.");
        
        // Update localStorage to reflect no unclaimed coins and close popup
        loginData.dailyCheckInResult = {
          ...loginData.dailyCheckInResult,
          claimed: true,
          unclaimedCoins: 0,
          closePopup: true,
          closePopupDate: new Date().toDateString()
        };
        storeLoginData(loginData);
        
        setShowModal(false);
        if (onClose) onClose();
      } else if (data.statusCode >= 500) {
        // Server errors - close popup but don't update claim status
        toast.error(responseBody.message || "There was an issue claiming coins. You can claim later.");
        
        loginData.dailyCheckInResult.closePopup = true;
        loginData.dailyCheckInResult.closePopupDate = new Date().toDateString();
        storeLoginData(loginData);
        
        setShowModal(false);
        if (onClose) onClose();
      }
    } finally {
      setIsLoading(false); // Set loading to false when API call completes
    }
  }

  return (
    <>
      <Modal
        animationType="fade"
        transparent={true}
        visible={showModal}
        onRequestClose={onCloseClick}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={onCloseClick}
        >
          <View style={styles.container}>
            <TouchableOpacity activeOpacity={1} style={styles.mainView}>
              <TouchableOpacity
                onPress={onCloseClick}
                activeOpacity={0.5}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                }}
              >
                <Image
                  source={AppImages.icCloseWhiteSvg}
                  style={{
                    height: 18,
                    width: 18,
                    resizeMode: "contain",
                  }}
                />
              </TouchableOpacity>
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.SEMIBOLD_ANYBODY_16
                    : AppFontStyle.SEMIBOLD_ANYBODY_22,
                  { color: "white", textAlign: "center", marginBottom: 12 },
                ]}
              >
                Daily Check-Ins
              </Text>
              <View
                style={[
                  {
                    alignItems: "center",
                    marginVertical: 10,
                    gap: 12,
                  },
                ]}
              >
                <ImageComponent
                  url={AppImages.icCoinBelt}
                  style={[
                    {
                      resizeMode: "contain",
                      width:
                        deviceSize === DEVICE_SIZES.SM ||
                        deviceSize === DEVICE_SIZES.XS
                          ? 61
                          : 110,
                      height:
                        deviceSize === DEVICE_SIZES.SM ||
                        deviceSize === DEVICE_SIZES.XS
                          ? 51
                          : 100,
                    },
                  ]}
                />
                <Text style={[AppFontStyle.REGULAR_ANYBODY_48, {}]}>
                  {totalCoins}
                </Text>
              </View>
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_14,
                  { textAlign: "center", marginVertical: 12 },
                ]}
              >
                Keep coming back every day and watch your stack grow
              </Text>
              <CoinView
                checkInData={checkInData}
                setDisableClaim={setDisableClaim}
              />
              <Separator
                color={AppColors.white}
                height={1}
                marginTop={12}
                marginBottom={12}
                marginHorizontal={0}
              />
              <View style={{ alignItems: "flex-end" }}>
                <TouchableOpacity
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: 24,
                    borderRadius: 8,
                    backgroundColor: AppColors.appViolentColor_6D08C3,
                    opacity: disableClaim || isLoading ? 0.5 : 1,
                  }}
                  disabled={disableClaim || isLoading}
                  onPress={async () => {
                    await updateDailyCheckInReward();
                  }}
                >
                  <Text
                    style={
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM
                        ? AppFontStyle.SEMIBOLD_ANYBODY_10
                        : AppFontStyle.SEMIBOLD_ANYBODY_14
                    }
                  >
                    {isLoading ? "Claiming..." : "Claim Coins"}
                  </Text>
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

const useStyles = CreateResponsiveStyle(
  {
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      justifyContent: "center",
      alignItems: "center",
    },
    container: {
      width: "100%",
      height: "100%",
      justifyContent: "center",
      alignItems: "center",
    },
    mainView: {
      // width: "61%",
      backgroundColor: AppColors.purple_453B50,
      padding: 24,
      margin: 30,
      alignSelf: "center",
      borderRadius: 22,
      borderWidth: 1,
      borderColor: AppColors.gray_A6A3AA,
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.SM]: {
      mainView: {
        width: "95%",
        padding: 12,
      },
    },
    [DEVICE_SIZES.XS]: {
      mainView: {
        width: "95%",
        padding: 12,
      },
    },
  }
);
